
  const CONTAINER_PADDING = 32

  export default {
    mounted() {
      this.pdfDocument = null
      this.totalPages = 0
      this.scale = 1.0
      this.totalTextItems = 0

      // Wait for element to be visible before initializing PDF viewer
      // This fixes scale calculation issues when viewer isn't visible yet (e.g., in tabs)
      this.waitForVisible(this.el, () => {
        this.waitForPdfJs()
          .then(() => {
            this.initializePDFViewer()
          })
          .catch((error) => {
            console.error("Failed to load PDF.js:", error)
            this.showError("Failed to load PDF viewer")
          })
      })
    },

    waitForVisible(element, callback) {
      // If already visible, run immediately
      if (element.offsetWidth > 0 && element.offsetHeight > 0) {
        callback()
        return
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.disconnect()
            callback()
          }
        })
      })
      observer.observe(element)
    },

    destroyed() {
      if (this.pdfDocument) {
        this.pdfDocument.destroy()
      }
    },

    async waitForPdfJs() {
      return new Promise((resolve, reject) => {
        if (window.pdfjsLib) {
          resolve()
          return
        }

        let attempts = 0
        const maxAttempts = 50

        const checkPdfJs = () => {
          if (window.pdfjsLib) {
            resolve()
          } else if (attempts >= maxAttempts) {
            reject(new Error("PDF.js failed to load"))
          } else {
            attempts++
            setTimeout(checkPdfJs, 100)
          }
        }

        checkPdfJs()
      })
    },

    async waitForContainer() {
      return new Promise((resolve, reject) => {
        const container = this.el.querySelector('[id$="-pdf-pages-container"]')
        if (container) {
          resolve(container)
          return
        }

        let attempts = 0
        const maxAttempts = 50

        const checkContainer = () => {
          const container = this.el.querySelector('[id$="-pdf-pages-container"]')
          if (container) {
            resolve(container)
          } else if (attempts >= maxAttempts) {
            reject(new Error("PDF pages container not found after waiting"))
          } else {
            attempts++
            setTimeout(checkContainer, 100)
          }
        }

        checkContainer()
      })
    },

    async initializePDFViewer() {
      let pagesContainer
      try {
        pagesContainer = await this.waitForContainer()
      } catch (error) {
        console.error("PDF pages container not found")
        return
      }

      this.pagesContainer = pagesContainer

      const pdfPath = this.el.dataset.pdfPath

      if (!pdfPath) {
        this.showError("No PDF path provided")
        return
      }

      try {
        await this.loadPDF(pdfPath)
      } catch (error) {
        console.error("Error loading PDF:", error)
        this.showError(`Failed to load PDF: ${error.message}`)
      }
    },

    async loadPDF(pdfPath) {
      const loadingTask = window.pdfjsLib.getDocument({
        url: pdfPath,
        cMapUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/",
        cMapPacked: true,
      })

      this.pdfDocument = await loadingTask.promise
      this.totalPages = this.pdfDocument.numPages
      console.log(`PDF loaded: ${this.totalPages} pages`)

      await this.renderAllPages()
    },

    async renderAllPages() {
      if (!this.pdfDocument) return

      const firstPage = await this.pdfDocument.getPage(1)
      const containerWidth = this.pagesContainer.clientWidth - CONTAINER_PADDING
      const initialViewport = firstPage.getViewport({ scale: 1.0 })
      this.scale = containerWidth / initialViewport.width

      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        await this.renderPage(pageNum)
      }
    },

    async renderPage(pageNumber) {
      if (!this.pdfDocument) return

      const page = await this.pdfDocument.getPage(pageNumber)
      const viewport = page.getViewport({ scale: this.scale })

      const pageContainer = document.createElement("div")
      pageContainer.className = "pdf-page-container mb-[10px]"
      pageContainer.style.cssText =
        "position: relative; background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.1);"

      const canvas = document.createElement("canvas")
      canvas.width = viewport.width
      canvas.height = viewport.height
      canvas.style.cssText = "display: block; width: 100%;"

      const context = canvas.getContext("2d")

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      }

      await page.render(renderContext).promise

      pageContainer.appendChild(canvas)

      // Add text layer for search and selection
      await this.renderTextLayer(page, pageContainer, viewport)

      this.pagesContainer.appendChild(pageContainer)

      console.log(`Rendered page ${pageNumber}/${this.totalPages}`)
    },

    async renderTextLayer(page, pageContainer, viewport) {
      try {
        const textContent = await page.getTextContent()
        this.totalTextItems += textContent.items.length

        if (textContent.items.length === 0) return

        const textLayerDiv = document.createElement("div")
        textLayerDiv.className = "pdf-text-layer"
        textLayerDiv.style.cssText = `
          position: absolute;
          left: 0;
          top: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          opacity: 0.2;
          line-height: 1.0;
          pointer-events: auto;
        `

        pageContainer.appendChild(textLayerDiv)

        await window.pdfjsLib.renderTextLayer({
          textContentSource: textContent,
          container: textLayerDiv,
          viewport: viewport,
        }).promise
      } catch (error) {
        console.warn(`Failed to render text layer for page: ${error.message}`)
      }
    },

    showError(message) {
      console.error("PDF Viewer Error:", message)

      const errorDiv = document.createElement("div")
      errorDiv.className =
        "pdf-error p-[10px] bg-red-50 border border-red-200 rounded-[2.5px] text-red-700 text-center"
      errorDiv.textContent = message

      this.pagesContainer.appendChild(errorDiv)
    },
  }
